import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Sparkles, AlertCircle, CheckCircle2, Loader2, User, PartyPopper } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { celebrationParagraphService } from '@/lib/api/celebrationParagraph.service';
import type { CelebrationParagraph, ParagraphStatus } from '@/types/tag.types';
import type { CelebrationCelebrant } from '@/types/user-contact.types';

const MIN_WORDS = 50;

interface ParagraphEditorProps {
  celebrationId: number;
  celebrants: CelebrationCelebrant[];
  onStatusChange?: (status: ParagraphStatus) => void;
  onParagraphsSaved?: () => void;
  initialCelebrationText?: string;
  initialCelebrantTexts?: Record<number, string>;
}

function wordCount(text: string): number {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

export const ParagraphEditor: React.FC<ParagraphEditorProps> = ({
  celebrationId,
  celebrants,
  onStatusChange,
  onParagraphsSaved,
  initialCelebrationText,
  initialCelebrantTexts,
}) => {
  const { t } = useTranslation('celebrations');
  const [, setParagraphs] = useState<CelebrationParagraph[]>([]);
  const [celebrationText, setCelebrationText] = useState('');
  const [celebrantTexts, setCelebrantTexts] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<ParagraphStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('celebration');

  // Load existing paragraphs
  useEffect(() => {
    loadParagraphs();
  }, [celebrationId]);

  const loadParagraphs = async () => {
    try {
      setLoading(true);
      const [paras, paragraphStatus] = await Promise.all([
        celebrationParagraphService.getParagraphs(celebrationId),
        celebrationParagraphService.getParagraphStatus(celebrationId),
      ]);

      setParagraphs(paras);
      setStatus(paragraphStatus);
      onStatusChange?.(paragraphStatus);

      // Populate text fields
      const celebrationPara = paras.find(p => p.paragraph_type === 'celebration');
      if (celebrationPara) {
        setCelebrationText(celebrationPara.content);
      } else if (initialCelebrationText && !celebrationText) {
        setCelebrationText(initialCelebrationText);
      }

      const celebrantMap: Record<number, string> = {};
      for (const p of paras) {
        if (p.paragraph_type === 'celebrant' && p.celebrant_id) {
          celebrantMap[p.celebrant_id] = p.content;
        }
      }

      if (initialCelebrantTexts) {
        for (const celebrant of celebrants) {
          if (!celebrantMap[celebrant.id]) {
            const prefillText = initialCelebrantTexts[celebrant.id];
            if (prefillText) {
              celebrantMap[celebrant.id] = prefillText;
            }
          }
        }
      }
      setCelebrantTexts(celebrantMap);
    } catch (err) {
      console.error('Failed to load paragraphs:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveParagraph = useCallback(
    async (type: 'celebration' | 'celebrant', celebrantId?: number) => {
      const key = type === 'celebration' ? 'celebration' : `celebrant-${celebrantId}`;
      const content = type === 'celebration' ? celebrationText : celebrantTexts[celebrantId!] || '';

      // Validate word count
      const words = wordCount(content);
      if (words < MIN_WORDS) {
        setErrors(prev => ({
          ...prev,
          [key]: t('paragraphEditor.errors.minWords', { minWords: MIN_WORDS, words }),
        }));
        return;
      }

      setErrors(prev => ({ ...prev, [key]: '' }));
      setSaving(prev => ({ ...prev, [key]: true }));

      try {
        await celebrationParagraphService.upsertParagraph(celebrationId, {
          paragraph_type: type,
          celebrant_id: celebrantId,
          content,
        });

        // Refresh status
        const newStatus = await celebrationParagraphService.getParagraphStatus(celebrationId);
        setStatus(newStatus);
        onStatusChange?.(newStatus);
        onParagraphsSaved?.();
      } catch (err: any) {
        setErrors(prev => ({
          ...prev,
          [key]: err?.response?.data?.message || t('paragraphEditor.errors.saveFailed'),
        }));
      } finally {
        setSaving(prev => ({ ...prev, [key]: false }));
      }
    },
    [celebrationId, celebrationText, celebrantTexts, onStatusChange, onParagraphsSaved]
  );

  const getTabId = (celebrant: CelebrationCelebrant) => `celebrant-${celebrant.id}`;

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">{t('paragraphEditor.loading')}</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            <CardTitle className="text-lg">{t('paragraphEditor.title')}</CardTitle>
          </div>
          {status && (
            <Badge variant={status.complete ? 'default' : 'secondary'}>
              {status.complete ? (
                <><CheckCircle2 className="h-3 w-3 mr-1" /> {t('paragraphEditor.complete')}</>
              ) : (
                <><AlertCircle className="h-3 w-3 mr-1" /> {t('paragraphEditor.incomplete')}</>
              )}
            </Badge>
          )}
        </div>
        <CardDescription>
          {t('paragraphEditor.descriptionLine1')}
          {t('paragraphEditor.descriptionLine2')}
          <span className="block mt-1 text-xs">{t('paragraphEditor.minWordsHint', { minWords: MIN_WORDS })}</span>
        </CardDescription>
      </CardHeader>

      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full justify-start mb-4 flex-wrap h-auto gap-1">
            <TabsTrigger value="celebration" className="flex items-center gap-1.5">
              <PartyPopper className="h-3.5 w-3.5" />
              {t('paragraphEditor.tabs.celebration')}
              {celebrationText && wordCount(celebrationText) >= MIN_WORDS && (
                <CheckCircle2 className="h-3 w-3 text-green-600" />
              )}
            </TabsTrigger>
            {celebrants.map(celebrant => {
              const celebrantName = celebrant.user_contact?.full_name || celebrant.name || celebrant.role;
              const text = celebrantTexts[celebrant.id] || '';
              const isComplete = wordCount(text) >= MIN_WORDS;
              return (
                <TabsTrigger
                  key={celebrant.id}
                  value={getTabId(celebrant)}
                  className="flex items-center gap-1.5"
                >
                  <User className="h-3.5 w-3.5" />
                  {celebrantName}
                  {isComplete && <CheckCircle2 className="h-3 w-3 text-green-600" />}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Celebration paragraph tab */}
          <TabsContent value="celebration">
            <ParagraphField
              label={t('paragraphEditor.fields.aboutCelebration.label')}
              description={t('paragraphEditor.fields.aboutCelebration.description')}
              value={celebrationText}
              onChange={setCelebrationText}
              onSave={() => saveParagraph('celebration')}
              saving={saving['celebration'] || false}
              error={errors['celebration']}
              t={t}
            />
          </TabsContent>

          {/* Celebrant paragraph tabs */}
          {celebrants.map(celebrant => {
            const celebrantName = celebrant.user_contact?.full_name || celebrant.name || celebrant.role;
            const key = `celebrant-${celebrant.id}`;
            return (
              <TabsContent key={celebrant.id} value={getTabId(celebrant)}>
                <ParagraphField
                  label={t('paragraphEditor.fields.aboutCelebrant.label', { name: celebrantName })}
                  description={t('paragraphEditor.fields.aboutCelebrant.description', { name: celebrantName })}
                  value={celebrantTexts[celebrant.id] || ''}
                  onChange={(text) =>
                    setCelebrantTexts(prev => ({ ...prev, [celebrant.id]: text }))
                  }
                  onSave={() => saveParagraph('celebrant', celebrant.id)}
                  saving={saving[key] || false}
                  error={errors[key]}
                  t={t}
                />
              </TabsContent>
            );
          })}

          {celebrants.length === 0 && (
            <div className="mt-4 p-4 border border-dashed rounded-lg text-center text-muted-foreground">
              <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t('paragraphEditor.noCelebrants.title')}</p>
              <p className="text-xs mt-1">
                {t('paragraphEditor.noCelebrants.description')}
              </p>
            </div>
          )}
        </Tabs>
      </CardContent>
    </Card>
  );
};

// ---------- Internal sub-component ----------

interface ParagraphFieldProps {
  label: string;
  description: string;
  value: string;
  onChange: (text: string) => void;
  onSave: () => void;
  saving: boolean;
  error?: string;
  t: (key: string, options?: Record<string, unknown>) => string;
}

const ParagraphField: React.FC<ParagraphFieldProps> = ({
  label,
  description,
  value,
  onChange,
  onSave,
  saving,
  error,
  t,
}) => {
  const words = wordCount(value);
  const isValid = words >= MIN_WORDS;

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-sm font-medium">{label}</Label>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <Textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={t('paragraphEditor.fields.placeholder', { minWords: MIN_WORDS })}
        rows={6}
        className="resize-y"
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <span className={isValid ? 'text-green-600 font-medium' : ''}>
            {t('paragraphEditor.wordCount', { words, minWords: MIN_WORDS })}
          </span>
          {isValid && <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />}
        </div>
        <Button
          onClick={onSave}
          disabled={saving || !value.trim()}
          size="sm"
          variant={isValid ? 'default' : 'outline'}
        >
          {saving ? (
            <>
              <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
              {t('paragraphEditor.actions.saving')}
            </>
          ) : (
            <>
              <Sparkles className="h-3.5 w-3.5 mr-1" />
              {t('paragraphEditor.actions.save')}
            </>
          )}
        </Button>
      </div>
      {error && (
        <p className="text-xs text-destructive flex items-center gap-1">
          <AlertCircle className="h-3 w-3" /> {error}
        </p>
      )}
    </div>
  );
};
